import { useEffect, useState } from 'react';
import { dashboardStats } from '@/lib/api';
import { formatTRY, SERVICE_LABELS } from '@/lib/commission';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { Wallet, TrendingUp, Users, Receipt as ReceiptIcon } from 'lucide-react';

const StatCard = ({ label, value, sub, icon: Icon, testId, tone = 'default' }) => {
  const toneMap = {
    default: 'bg-white',
    primary: 'bg-gradient-to-br from-white to-salon-secondary/40',
    success: 'bg-white',
  };
  return (
    <Card
      data-testid={testId}
      className={`hover-lift border-border rounded-2xl shadow-soft ${toneMap[tone]}`}
    >
      <CardContent className="p-6">
        <div className="mb-3 flex items-center justify-between">
          <span className="text-sm text-salon-muted">{label}</span>
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-salon-secondary/60 text-salon-primary-hover">
            <Icon size={18} strokeWidth={1.8} />
          </div>
        </div>
        <div className="number-display text-3xl text-salon-ink md:text-4xl">{value}</div>
        {sub && <div className="mt-2 text-xs text-salon-muted">{sub}</div>}
      </CardContent>
    </Card>
  );
};

const SERVICE_COLORS = ['#1F3A5F', '#5A82B5', '#DCE3EE', '#8BA892'];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dashboardStats()
      .then((d) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32 rounded-2xl" />)}
        </div>
        <Skeleton className="h-80 rounded-2xl" />
      </div>
    );
  }

  const today = data?.today || {};
  const month = data?.this_month || {};
  const all = data?.all_time || {};
  const trend = (data?.monthly_trend || []).map((t) => ({
    name: t.label,
    Ciro: t.total_amount,
    Personel: t.total_staff_share,
    Merkez: t.total_center_share,
  }));
  const svc = data?.service_distribution || { hand: 0, foot: 0, eyebrow: 0 };
  const serviceData = Object.entries(svc)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: SERVICE_LABELS[k], value: v }));

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <header className="flex flex-col gap-2">
        <div className="text-xs uppercase tracking-widest text-salon-primary">Genel Bakış</div>
        <h1 className="font-display text-4xl text-salon-ink md:text-5xl">Panel</h1>
        <p className="text-sm text-salon-muted">
          Bugünün ve bu ayın kazanç özeti, personel/merkez payları.
        </p>
      </header>

      <section className="grid grid-cols-1 gap-6 md:grid-cols-4">
        <StatCard
          testId="stat-today-revenue"
          label="Bugünkü Ciro"
          value={formatTRY(today.total_amount)}
          sub={`${today.transaction_count || 0} işlem`}
          icon={Wallet}
          tone="primary"
        />
        <StatCard
          testId="stat-month-revenue"
          label="Bu Ay Ciro"
          value={formatTRY(month.total_amount)}
          sub={`${month.transaction_count || 0} işlem`}
          icon={TrendingUp}
        />
        <StatCard
          testId="stat-month-staff"
          label="Bu Ay Personel Payı"
          value={formatTRY(month.total_staff_share)}
          sub="Net (KDV & komisyon düşülmüş)"
          icon={Users}
        />
        <StatCard
          testId="stat-month-center"
          label="Bu Ay Merkez Payı"
          value={formatTRY(month.total_center_share)}
          sub="Net (KDV & komisyon düşülmüş)"
          icon={ReceiptIcon}
        />
      </section>

      <section className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="rounded-2xl border-border shadow-soft lg:col-span-2" data-testid="chart-monthly-trend">
          <CardContent className="p-6">
            <div className="mb-6 flex items-baseline justify-between">
              <div>
                <h2 className="font-display text-2xl text-salon-ink">Aylık Ciro Trendi</h2>
                <p className="text-xs text-salon-muted">Son 6 ay</p>
              </div>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trend} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(34,34,34,0.06)" />
                  <XAxis dataKey="name" stroke="#5A6A80" fontSize={12} />
                  <YAxis stroke="#5A6A80" fontSize={12} tickFormatter={(v) => `${v} TL`} />
                  <Tooltip
                    formatter={(v) => formatTRY(v)}
                    contentStyle={{ borderRadius: 12, border: '1px solid rgba(15,29,51,0.08)' }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Ciro" fill="#1F3A5F" radius={[8, 8, 0, 0]} />
                  <Bar dataKey="Personel" fill="#8BA892" radius={[8, 8, 0, 0]} />
                  <Bar dataKey="Merkez" fill="#DCE3EE" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-border shadow-soft" data-testid="chart-service-distribution">
          <CardContent className="p-6">
            <h2 className="mb-1 font-display text-2xl text-salon-ink">İşlem Dağılımı</h2>
            <p className="mb-4 text-xs text-salon-muted">Tüm zamanlar</p>
            {serviceData.length === 0 ? (
              <div className="flex h-64 items-center justify-center text-sm text-salon-muted">
                Henüz işlem yok
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={serviceData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={3}>
                      {serviceData.map((_, i) => (
                        <Cell key={i} fill={SERVICE_COLORS[i % SERVICE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid rgba(15,29,51,0.08)' }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </section>

      <section className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <Card className="rounded-2xl border-border shadow-soft" data-testid="stat-alltime-count">
          <CardContent className="p-6">
            <div className="text-xs uppercase tracking-widest text-salon-muted">Toplam İşlem</div>
            <div className="number-display mt-2 text-3xl text-salon-ink">{all.transaction_count || 0}</div>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-border shadow-soft" data-testid="stat-alltime-staff">
          <CardContent className="p-6">
            <div className="text-xs uppercase tracking-widest text-salon-muted">Toplam Personel Payı</div>
            <div className="number-display mt-2 text-3xl text-salon-ink">{formatTRY(all.total_staff_share)}</div>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-border shadow-soft" data-testid="stat-alltime-center">
          <CardContent className="p-6">
            <div className="text-xs uppercase tracking-widest text-salon-muted">Toplam Merkez Payı</div>
            <div className="number-display mt-2 text-3xl text-salon-ink">{formatTRY(all.total_center_share)}</div>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
